// src/hooks/useSocket.js — Stable WebSocket Singleton v19
// ROOT CAUSE FIX: getSocket() was recreating the socket when existing socket
// was in reconnecting state (connected=false, isConnecting=false) — caused
// multiple socket instances → rapid connect/disconnect loop.
//
// Architecture:
//   - ONE socket instance for the entire app lifetime (module-level singleton)
//   - Socket created ONCE at module load — never recreated
//   - Token passed in handshake auth (more reliable than post-connect emit)
//   - Reconnection handled entirely by socket.io-client internals
//   - Manual reconnect only for 'io server disconnect' (server kicked us)
import { useEffect, useRef } from 'react';

const WS_URL = (
  process.env.REACT_APP_WS_URL ||
  process.env.REACT_APP_API_URL?.replace('/api', '') ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.papercrypto.app')
);

const HEARTBEAT_MS = 25000; // must match backend pingInterval

// ── Dev-only logger — silent in production builds ───────────────────────────
const dlog = process.env.NODE_ENV !== 'production'
  ? (...args) => console.log(...args)
  : () => {};

// ── Module-level singleton state ─────────────────────────────────────────────
let _socket      = null;   // the one and only socket instance
let _heartbeat   = null;   // heartbeat interval id
let _initialized = false;  // have we called io() yet?

// ── S-191: socket-replacement registry ───────────────────────────────────────
// The header above says "Socket created ONCE at module load — never recreated".
// That stopped being true when disconnectSocket() was added: it nulls _socket on
// logout, so the next getSocket() builds a NEW Socket object.
//
// Every listener attached to the old object is silently orphaned. The two that
// matter are the price handlers in AppContext (deps `[]`, so their effect can never
// re-run) — after a logout→login the webapp keeps rendering, the server keeps
// sending, and prices never move again until a full page reload.
//
// Rather than a React state counter (which would re-render the whole provider tree
// on every replacement), subscribers register a plain callback here and re-attach
// themselves imperatively. No re-render, no dependency-array changes.
//
// ORDERING IS CRITICAL: _socket must be nulled BEFORE notifying. If we notify first,
// subscribers call getSocket(), receive the socket we are about to destroy, and
// re-attach to a corpse — the bug would survive the fix and look identical.
const _socketReplacedSubs = new Set();

export function onSocketReplaced(cb) {
  if (typeof cb !== 'function') return () => {};
  _socketReplacedSubs.add(cb);
  return () => { _socketReplacedSubs.delete(cb); };
}

function _notifySocketReplaced() {
  for (const cb of Array.from(_socketReplacedSubs)) {
    // One bad subscriber must never stop the others from re-attaching.
    try { cb(); } catch (e) { dlog('[WS] socket-replaced subscriber threw', e); }
  }
}

// ── Create the socket ONCE ───────────────────────────────────────────────────
function createSocket() {
  if (_initialized) return _socket; // guard: never call io() twice
  _initialized = true;

  try {
    const { io } = require('socket.io-client');
    const token  = localStorage.getItem('pc_token');

    dlog('[WS] Creating socket → ', WS_URL);

    _socket = io(WS_URL, {
      transports:            ['websocket', 'polling'],
      // Pass token in handshake so backend authenticates before first event
      // This avoids the post-connect 'auth' emit that triggered the disconnect loop
      auth:                  token ? { token } : {},
      reconnection:          true,
      reconnectionAttempts:  Infinity,          // keep trying — server may restart
      reconnectionDelay:     1000,              // 1s first retry
      reconnectionDelayMax:  30000,             // cap at 30s
      randomizationFactor:   0.3,              // ±30% jitter to prevent thundering herd
      timeout:               10000,
      autoConnect:           true,
      // IMPORTANT: forceNew: false (default) ensures we never create a second connection
      forceNew:              false,
    });

    // ── Connect ──────────────────────────────────────────────────────────────
    _socket.on('connect', () => {
      dlog('[WS] ✅ Connected — id:', _socket.id);
      _startHeartbeat();
      // Join all_prices room on every (re)connect
      _socket.emit('subscribe_all_prices');
      // If token exists but wasn't in handshake (e.g. logged in after socket created),
      // send auth via emit as well — backend handles both paths
      const t = localStorage.getItem('pc_token');
      if (t && !_socket.handshake?.auth?.token) {
        _socket.emit('auth', t);
      }
    });

    // ── Disconnect ───────────────────────────────────────────────────────────
    _socket.on('disconnect', (reason) => {
      dlog('[WS] ⚠️  Disconnected — reason:', reason);
      _stopHeartbeat();
      if (reason === 'io server disconnect') {
        // Server explicitly disconnected us (e.g. server restart)
        // socket.io won't auto-reconnect for this reason — trigger manually
        dlog('[WS] Server disconnect detected — reconnecting in 2s...');
        setTimeout(() => {
          if (_socket && !_socket.connected) _socket.connect();
        }, 2000);
      }
      // For all other reasons (ping timeout, transport close, etc.)
      // socket.io-client handles reconnection automatically via reconnectionDelay
    });

    // ── Connect error ─────────────────────────────────────────────────────────
    _socket.on('connect_error', (err) => {
      console.warn('[WS] ❌ Connect error:', err.message);
      // socket.io auto-retries — no action needed here
    });

    // ── Reconnect events (debug) ──────────────────────────────────────────────
    _socket.io.on('reconnect', (attempt) => {
      dlog(`[WS] 🔄 Reconnected after ${attempt} attempt(s)`);
    });
    _socket.io.on('reconnect_attempt', (attempt) => {
      dlog(`[WS] 🔄 Reconnect attempt #${attempt}`);
    });
    _socket.io.on('reconnect_failed', () => {
      console.error('[WS] ❌ Reconnect failed — giving up (max attempts reached)');
    });

    _socket.on('pong', () => {});       // heartbeat ack — no-op
    _socket.on('auth_ok',    (d) => { dlog('[WS] Auth OK — user:', d.user_id); });
    _socket.on('auth_error', (d) => { console.warn('[WS] Auth error:', d.message); });

  } catch (err) {
    console.error('[WS] Failed to create socket:', err);
    // Return a safe no-op stub so the rest of the app doesn't crash
    _socket = {
      on: () => {}, off: () => {}, emit: () => {},
      connected: false, id: 'noop',
      handshake: { auth: {} },
    };
  }

  return _socket;
}

// ── Heartbeat ─────────────────────────────────────────────────────────────────
function _startHeartbeat() {
  _stopHeartbeat();
  _heartbeat = setInterval(() => {
    if (_socket?.connected) _socket.emit('ping');
  }, HEARTBEAT_MS);
}
function _stopHeartbeat() {
  if (_heartbeat) { clearInterval(_heartbeat); _heartbeat = null; }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * getSocket() — returns the singleton socket, creating it once if needed.
 * Safe to call from anywhere, any number of times.
 * NEVER recreates the socket — always returns the same instance.
 */
export function getSocket() {
  if (!_socket) createSocket();
  return _socket;
}

/**
 * authenticateSocket(token) — sends auth token to backend.
 * Emits 'auth' event if socket connected, or waits for next connect.
 * Called after user logs in (token wasn't available when socket was created).
 */
export function authenticateSocket(token) {
  if (!token) return;
  const s = getSocket();
  // v194: guard against socket not being ready or missing methods
  if (!s || typeof s.emit !== 'function') return;
  if (s.connected) {
    s.emit('auth', token);
  } else if (typeof s.once === 'function') {
    // Socket is reconnecting — attach one-time listener
    s.once('connect', () => s.emit('auth', token));
  } else if (typeof s.on === 'function') {
    // Fallback if .once() is unavailable
    const handler = () => { s.emit('auth', token); if (typeof s.off === 'function') s.off('connect', handler); };
    s.on('connect', handler);
  }
}

/**
 * updateSocketToken(token) — update the auth token used in future reconnects.
 * Call after login so reconnections include the new token in handshake.
 */
export function updateSocketToken(token) {
  const s = getSocket();
  if (s?.auth !== undefined) {
    s.auth = { token };
  }
  if (token && s?.io?.opts) {
    s.io.opts.auth = { token };
  }
  authenticateSocket(token);
}

/**
 * disconnectSocket() — call on logout to cleanly close the connection.
 * Resets the singleton so a new socket is created after next login.
 */
export function disconnectSocket() {
  _stopHeartbeat();
  if (_socket) {
    _socket.disconnect();
    _socket = null;
  }
  _initialized = false;
  dlog('[WS] Socket disconnected (logout)');
  // S-191: notify LAST, after _socket is null, so subscribers calling getSocket()
  // build/receive the replacement rather than the object we just destroyed.
  _notifySocketReplaced();
}

/**
 * useSocketEvent(event, handler) — React hook that subscribes to a socket event.
 * Automatically cleans up listener on component unmount.
 * Handler ref is always current — no stale closure issues.
 */
export function useSocketEvent(event, handler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const wrapper = (...args) => handlerRef.current?.(...args);
    let bound = null;

    const attach = () => {
      const s = getSocket();
      if (!s || typeof s.on !== 'function') return;
      if (bound === s) return;            // already bound to this exact instance
      if (bound && typeof bound.off === 'function') {
        try { bound.off(event, wrapper); } catch (_) {}
      }
      s.on(event, wrapper);
      bound = s;
    };

    attach();
    // S-191: re-attach whenever the singleton is replaced (logout → login). Without
    // this the listener stays bound to the destroyed object and never fires again.
    const unsub = onSocketReplaced(attach);

    return () => {
      unsub();
      if (bound && typeof bound.off === 'function') {
        try { bound.off(event, wrapper); } catch (_) {}
      }
      bound = null;
    };
  }, [event]); // event is a string constant — safe dep
}

/**
 * usePriceSocket(onPrice) — convenience hook for price updates.
 */
export function usePriceSocket(onPrice) {
  useSocketEvent('price_update', onPrice);
}
